import { Injectable } from '@angular/core';
import { CoordinatePointerOverlayHelper } from 'libs/shared/src/lib/geo/coordinate-pointer-overlay.helper';
import { MapPlacePredictionsService } from 'libs/ubi-map/src/lib/services/map-place-predictions.service';
import { AdvancedMapPlacePredictionsService } from 'libs/advanced-map/src/lib/services/advanced-map-place-predictions.service';
import {
  isCoordinateOrigin,
  parseCoordinateLocationJson,
} from './coordinate-location-payload.util';
import { CoordinateMapContextService } from './coordinate-map-context.service';
import { buildViewportAroundPoint } from './coordinate-viewport.util';
import { GeoNodePoint, GeoNodeSearchService } from './geo-node-search.service';
import { CoordinatePointerDisplayOptions } from './models/coordinate-pointer-options.model';
import { CoordinateMapContext } from './models/coordinate-map-context.model';
import { LatLngBoundsLiteral } from './models/coordinate-search.model';

const MAP_READY_MAX_ATTEMPTS = 30;
const MAP_READY_RETRY_MS = 200;
const NEARBY_RADIUS_METERS = 300;
const FALLBACK_VIEWPORT_METERS = 500;

@Injectable({ providedIn: 'root' })
export class CoordinateMapControllerService {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly legacyPointerOverlay = new CoordinatePointerOverlayHelper();

  constructor(
    private readonly geoNodeSearch: GeoNodeSearchService,
    private readonly contextService: CoordinateMapContextService,
    private readonly mapPlacePredictions: MapPlacePredictionsService,
    private readonly advancedMapPlacePredictions: AdvancedMapPlacePredictionsService
  ) {}

  handleCoordinateSelection(
    locationJson: string,
    context?: CoordinateMapContext,
    attempt = 0
  ): void {
    const location = parseCoordinateLocationJson(locationJson);
    if (!isCoordinateOrigin(location)) {
      return;
    }

    const ctx =
      context ??
      this.contextService.resolve(
        this.mapPlacePredictions,
        this.advancedMapPlacePredictions
      );
    const map = this.resolveGoogleMap(ctx);

    if (!map) {
      if (attempt < MAP_READY_MAX_ATTEMPTS) {
        this.clearRetryTimer();
        this.retryTimer = setTimeout(
          () => this.handleCoordinateSelection(locationJson, context, attempt + 1),
          MAP_READY_RETRY_MS
        );
      }
      return;
    }

    this.clearRetryTimer();

    const nearby = this.findNearby(location.lat, location.lng, ctx);
    this.fitMapToCoordinate(map, location, nearby);
    this.updatePointer(location.lat, location.lng, location.name, ctx);
  }

  clearCoordinatePointer(context?: CoordinateMapContext): void {
    this.clearRetryTimer();
    const ctx =
      context ??
      this.contextService.resolve(
        this.mapPlacePredictions,
        this.advancedMapPlacePredictions
      );
    ctx.mvtMap?.clearCoordinatePointer?.();
    ctx.advancedMap?.clearCoordinatePointer?.();
    this.legacyPointerOverlay.clear();
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private fitMapToCoordinate(
    map: google.maps.Map,
    location: { lat: number; lng: number; viewport?: LatLngBoundsLiteral },
    nearby: GeoNodePoint[]
  ): void {
    const bounds = new google.maps.LatLngBounds();

    if (nearby.length > 0) {
      bounds.extend(new google.maps.LatLng(location.lat, location.lng));
      nearby.forEach((n) =>
        bounds.extend(new google.maps.LatLng(n.lat, n.lng))
      );
      map.fitBounds(bounds);
      return;
    }

    const viewport =
      location.viewport ??
      buildViewportAroundPoint(location.lat, location.lng, FALLBACK_VIEWPORT_METERS);
    bounds.extend(new google.maps.LatLng(viewport.south, viewport.west));
    bounds.extend(new google.maps.LatLng(viewport.north, viewport.east));
    map.fitBounds(bounds);
  }

  private findNearby(
    lat: number,
    lng: number,
    context: CoordinateMapContext
  ): GeoNodePoint[] {
    if (!context.showMvtMap && context.advancedMap) {
      return this.geoNodeSearch.findNearbyNodes(
        lat,
        lng,
        context.advancedMap,
        NEARBY_RADIUS_METERS
      );
    }

    if (context.fallbackNodes?.length) {
      return this.geoNodeSearch.findNearbyFromNodeList(
        lat,
        lng,
        context.fallbackNodes,
        context.latField ?? 'y',
        context.lngField ?? 'x',
        NEARBY_RADIUS_METERS
      );
    }

    if (context.advancedMap) {
      return this.geoNodeSearch.findNearbyNodes(
        lat,
        lng,
        context.advancedMap,
        NEARBY_RADIUS_METERS
      );
    }

    return [];
  }

  private resolveGoogleMap(context: CoordinateMapContext): google.maps.Map | null {
    if (context.showMvtMap) {
      return context.mvtMap?.getGoogleMap?.() ?? null;
    }
    return (
      context.advancedMap?.getMap?.() ??
      context.googleMap ??
      null
    );
  }

  private updatePointer(
    lat: number,
    lng: number,
    label: string | undefined,
    context: CoordinateMapContext
  ): void {
    const displayOptions: CoordinatePointerDisplayOptions = {
      showInfoWindow: context.showCoordinatePointerInfoWindow !== false,
      label: label ?? `${lat},${lng}`,
      onClose: () => this.clearCoordinatePointer(context),
    };

    const activeHost = context.showMvtMap
      ? context.mvtMap
      : context.advancedMap;

    if (activeHost?.setCoordinatePointer) {
      activeHost.setCoordinatePointer(lat, lng, displayOptions);
      return;
    }

    const map = this.resolveGoogleMap(context);
    if (!map) {
      return;
    }

    this.legacyPointerOverlay.setPointer({
      map,
      lat,
      lng,
      label: displayOptions.label,
      showInfoWindow: displayOptions.showInfoWindow,
      onClose: displayOptions.onClose ?? (() => undefined),
    });
  }
}
